/**
 * Formats a file size in bytes to a human-readable string.
 * Automatically selects the appropriate unit (B, KB, MB) based on size.
 *
 * - Files < 1 KB: Shows in bytes (e.g., "512 B")
 * - Files < 1 MB: Shows in KB with no decimals (e.g., "256 KB")
 * - Files >= 1 MB: Shows in MB with 1 decimal place (e.g., "2.5 MB")
 *
 * @param bytes - The file size in bytes
 * @returns Formatted file size string
 *
 * @example
 * formatBytes(1024);        // "1 KB"
 * formatBytes(2048000);     // "2.0 MB"
 * formatBytes(512);         // "512 B"
 */
/** Default maximum size for student file-upload answers (block attachments). */
export const MAX_ATTACHMENT_FILE_SIZE = 200 * 1024 * 1024;

/**
 * Raised maximum used when the `enable_1gb_upload` feature flag is on for the
 * tenant or assessment. Falls back to {@link MAX_ATTACHMENT_FILE_SIZE}.
 */
export const LARGE_ATTACHMENT_FILE_SIZE = 1024 * 1024 * 1024;

/** Combined size above which a bulk (zip) download is flagged as slow. */
export const BULK_DOWNLOAD_WARN_BYTES = 2 * 1024 * 1024 * 1024;

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Warning message for a bulk download that is large enough to be slow to zip.
 *
 * Returns null for single-file downloads (they stream directly, no zipping) and
 * when the combined size is within {@link BULK_DOWNLOAD_WARN_BYTES}.
 */
export function bulkDownloadWarning(
  files: ReadonlyArray<{ fileSize?: number | null }>
): string | null {
  if (files.length < 2) return null;
  const total = files.reduce((sum, file) => sum + (file.fileSize ?? 0), 0);
  if (total <= BULK_DOWNLOAD_WARN_BYTES) return null;
  return `Downloading ${formatBytes(total)} at once can be slow. For large files, download them individually instead.`;
}
